/**
 * Catering Menu PDF Page - Bulaccino Cafe
 * Printable/downloadable PDF-style menu page
 */
import { useState, useEffect, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, Printer, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '../utils/apiClient';

// Contact info
const CONTACT_INFO = {
  phone: '+679 675 0109',
  email: 'bulaccinocafe@gmail.com',
  address: 'Shop 2 & 3 Concave Drive, Namaka Location - Nadi, Fiji',
  website: 'www.BulaccinoCafe.com',
  hours: 'Open 7 days per week - 7:00 am - 6:00 pm',
  tagline: 'We Do Catering, Cakes, Office Meetings, Promotions, Farewells, Birthdays - Any Occasion That You Can Think Of!'
};

// Category display names
const CATEGORY_NAMES = {
  sweets: 'Morning & Afternoon Tea - Sweets',
  hot_snacks: 'Hot Snacks & Fillers',
  salads: 'Fresh Salads',
  quiches: 'Quiches',
  sandwiches: 'Sandwiches',
  wraps: 'Wraps',
  platters: 'Platters',
  drinks: 'Beverages',
  extras: 'Extras & Services',
  cakes: 'Cakes & Celebration'
};

const CATEGORY_ORDER = ['sweets', 'hot_snacks', 'salads', 'quiches', 'sandwiches', 'wraps', 'platters', 'drinks', 'extras', 'cakes'];

export default function CateringMenuPDF() {
  const [menuCategories, setMenuCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const printRef = useRef(null);

  useEffect(() => {
    fetchMenu();
  }, []);

  const fetchMenu = async () => {
    try {
      const res = await api.get('/catering/menu');
      setMenuCategories(res.data.categories || {});
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <div className="print:hidden fixed top-4 right-4 z-50 flex gap-2">
        <Button
          onClick={handlePrint}
          className="bg-amber-600 hover:bg-amber-700 text-white shadow-lg"
          data-testid="catering-print-menu-btn"
        >
          <Printer className="w-4 h-4 mr-2" />
          Print Menu
        </Button>
        <Button
          onClick={() => window.close()}
          variant="outline"
          className="bg-white shadow-lg"
        >
          Close
        </Button>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 15mm;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .page-break {
            page-break-before: always;
          }
        }
      `}</style>

      {/* PDF Content */}
      <div ref={printRef} className="bg-white min-h-screen">
        {/* Header */}
        <div className="text-center py-8 border-b-4 border-amber-600 mb-8">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">Bulaccino Cafe</h1>
          <h2 className="text-2xl text-amber-700 mb-4">Corporate Catering Menu</h2>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto px-4">
            {CONTACT_INFO.tagline}
          </p>
        </div>

        {/* Contact Info Bar */}
        <div className="bg-amber-50 px-6 py-4 mb-8 flex flex-wrap justify-center gap-6 text-sm text-amber-900">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            <span>{CONTACT_INFO.phone}</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            <span>{CONTACT_INFO.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{CONTACT_INFO.hours}</span>
          </div>
        </div>

        {/* Important Notes */}
        <div className="max-w-4xl mx-auto px-6 mb-8">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h3 className="font-bold text-amber-900 mb-2">Important Information</h3>
            <ul className="text-sm text-amber-800 space-y-1">
              <li>• 10% surcharge applies to all orders for packaging & presentation</li>
              <li>• 70% deposit required upon order confirmation</li>
              <li>• Delivery charges may apply based on location</li>
              <li>• Buffet setup and staffing available upon request</li>
              <li>• Minimum order quantities apply to most items</li>
              <li>• Please place orders at least 48 hours in advance</li>
            </ul>
          </div>
        </div>

        {/* Menu Categories */}
        <div className="max-w-4xl mx-auto px-6 pb-12">
          {CATEGORY_ORDER.map((category, catIndex) => {
            const items = menuCategories[category] || [];
            if (items.length === 0) return null;
            
            // Add page break before certain categories for better print layout
            const needsPageBreak = ['quiches', 'platters', 'cakes'].includes(category) && catIndex > 0;
            
            return (
              <div 
                key={category} 
                className={`mb-8 ${needsPageBreak ? 'page-break' : ''}`}
              >
                {/* Category Header */}
                <div className="bg-amber-600 text-white px-4 py-2 rounded-t-lg">
                  <h3 className="text-lg font-bold">{CATEGORY_NAMES[category] || category}</h3>
                </div>
                
                {/* Items Table */}
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-amber-50 text-left text-sm font-medium text-amber-900">
                      <th className="px-4 py-2 border border-amber-200">Item</th>
                      <th className="px-4 py-2 border border-amber-200 w-24">Unit</th>
                      <th className="px-4 py-2 border border-amber-200 w-20 text-center">Min Qty</th>
                      <th className="px-4 py-2 border border-amber-200 w-24 text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr 
                        key={item.item_id} 
                        className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        <td className="px-4 py-2 border border-gray-200">
                          <div className="font-medium text-gray-900">{item.name}</div>
                          {item.description && (
                            <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 text-sm text-gray-600">
                          {item.unit}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 text-sm text-gray-600 text-center">
                          {item.min_qty}
                        </td>
                        <td className="px-4 py-2 border border-gray-200 text-right font-semibold text-amber-700">
                          {item.price > 0 ? `$${item.price.toFixed(2)}` : 'POA'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="bg-amber-900 text-white py-8 px-6 text-center">
          <h3 className="text-xl font-bold mb-4">Ready to Order?</h3>
          <p className="text-amber-100 mb-4">
            Contact us to discuss your catering needs
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <span>{CONTACT_INFO.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <span>{CONTACT_INFO.email}</span>
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4 text-amber-200 text-sm">
            <MapPin className="w-4 h-4" />
            <span>{CONTACT_INFO.address}</span>
          </div>
          <p className="mt-6 text-amber-300 text-xs">
            {CONTACT_INFO.website} • Ask about our Bula Coffee Pass!
          </p>
        </div>
      </div>
    </>
  );
}
